// Data provider for the Alt Surface Builder screen (IM-2486).
//
// Fetches and caches every input the generated-token surface methodology will
// need, and exposes them through one hook. This file is the data plumbing ONLY
// — it computes NO surface (no stdev / EWMA / ratio / ATM-scaling). Those are
// the following story; the frame assembled here is their raw input.
//
// What it provides, per selected token (base_quote):
//   dailyCloses          wide UTC-daily-last-close frame for ETH / BTC / token
//   ethSurface           ETH reference surface  (Exchange/Immersive, via base_quote)
//   btcSurface           BTC reference surface  (discounting leg)
//   publishedTokenSurface the token's AUTO-GENERATED surface (tie-out source)
//   tokenSpot            latest token price (see FLAGGED spot note below)
//   isLoading / error    honest aggregate status
//
// Caching: ETH/BTC candle series are shared across tokens (module scope); the
// token series is cached per base_quote. Each series is fetched ONCE — the TEST
// gateway is single-threaded and the token candle is ~365 hourly rows, so we
// never refetch on re-render or token re-selection.
import { useContext, useEffect, useMemo, useRef, useState } from 'react';

import { WebSocketContext } from '../websocket-context/websocket-provider';
import { SnapshotRequest } from '../model/snapshotData';
import { SnapshotTypeEnum } from '../../model/snapshot_type';
import { SUBSCRIPTIONS_KEY } from '../../constants/websocket-events';
import { CandleStick } from '../model/candleData';
import {
  assembleDailyCloseFrame,
  assembleHourlyCloseFrame,
  BTC_PERP_TARGET,
  buildPerpCandleRequest,
  DAILY_CLOSE_COLUMNS,
  DAILY_CLOSE_HISTORY_DAYS,
  DailyCloseRow,
  DAY_MS,
  ETH_PERP_TARGET,
  HOUR_MS,
  HourlyPoint,
  latestTokenClose,
  PerpCandleTarget,
  resolveTokenPerpTarget,
} from './alt-surface-daily-closes';
import type { AvailableToken } from '../exotics-tokens/use-available-tokens';

const CANDLE_TIMEOUT_MS = 20_000;
const SURFACE_TIMEOUT_MS = 15_000;

// Raw vol-surface payload off the wire. Kept deliberately loose for IM-2486
// (the frame carries it untouched); IM-2487 tightens this into a typed surface
// shape when it consumes the ETH/BTC/token surfaces.
type SurfacePayload = Record<string, unknown>;

export interface AltSurfaceInputs {
  dailyCloses: DailyCloseRow[];
  // The raw HOURLY wide frame (ETH/BTC/token on the hourly timestamps),
  // assembled from the SAME fetched candles as dailyCloses — before the daily
  // resample. The compute picks this frame when the hourly-return frequency is
  // selected; no extra network is incurred.
  hourlyCloses: DailyCloseRow[];
  ethSurface: SurfacePayload | null;
  btcSurface: SurfacePayload | null;
  publishedTokenSurface: SurfacePayload | null;
  tokenSpot: number | null;
  isLoading: boolean;
  error: string | null;
}

// Module-scope candle cache. ETH/BTC are keyed by a fixed string; the token
// series is keyed by base_quote so switching tokens is cheap and never refires
// the ETH/BTC fetches.
const candleCache = new Map<string, HourlyPoint[]>();
const candleInflight = new Map<string, Promise<HourlyPoint[]>>();

type WsSend = (msg: string) => void;
type WsSubscribe = (id: string, handler: (resp: unknown) => void) => void;

export interface WsHandle {
  sendMessage: WsSend;
  subscribeToChannel: WsSubscribe;
  unsubscribeFromChannel?: (id: string) => void;
}

// Parses one candlestick result row into a UTC-epoch hourly point, reusing the
// production CandleStick parser (handles the "YYYY-MM-DD HH:MM:SS" UTC wire
// format). We take the bar's OPEN, not its close: candlestick and the
// generator's GetTimeSeries read the same tick table, differing only in which
// tick per hour they return — close is the last tick, open is the FIRST tick,
// which is exactly the value GetTimeSeries/the generator sample. Using open
// ties the realised-vol inputs out to the published surface (IM-2495). The
// `close` field denotes the per-bar sample value, now the open. NOTE this also
// underpins fetchCandles' end=now determinism: the first tick is fixed within an
// hour, so the current partial bar's sample does not drift with the end minute —
// do not revert this to candle.close without re-flooring `end`. Rows that fail to
// parse to a finite value are surfaced as NaN and dropped downstream.
const toHourlyPoint = (row: unknown): HourlyPoint => {
  try {
    const candle = new CandleStick(row as unknown[]);
    return { timeMs: candle.time * 1000, close: parseFloat(candle.open) };
  } catch {
    return { timeMs: NaN, close: NaN };
  }
};

// One-shot candlestick fetch for a perp target, cached by `cacheKey`. Mirrors
// the request/response shape used by timeseries-provider's PERPETUAL path and
// the one-shot pattern in use-futures-snapshot.
const fetchCandles = (
  ws: WsHandle,
  cacheKey: string,
  target: PerpCandleTarget,
): Promise<HourlyPoint[]> => {
  const cached = candleCache.get(cacheKey);
  if (cached) return Promise.resolve(cached);
  const existing = candleInflight.get(cacheKey);
  if (existing) return existing;

  // `begin` is hour-aligned; `end` runs to now. The candlestick server buckets
  // hourly bars from `begin` (origin := begin), so an unaligned `begin` phase-
  // shifts every bar off the hour and makes the realised-vol ratios depend on
  // the load minute — `begin` must be floored. `end` is deliberately NOT floored:
  // it includes the current partial UTC day, matching the generator's up-to-now
  // read, so reactive short-dated tokens track the published surface across the
  // UTC day boundary instead of lagging it by a day. The bucket phase is set by
  // `begin` alone, and the current day's sample is the bar's first tick (see
  // toHourlyPoint), which is stable regardless of the end minute — so end=now
  // stays deterministic.
  const begin = new Date(
    Math.floor(Date.now() / HOUR_MS) * HOUR_MS - DAILY_CLOSE_HISTORY_DAYS * DAY_MS,
  );
  const end = new Date();

  const promise = new Promise<HourlyPoint[]>((resolve, reject) => {
    const reqId = `ALT_SURFACE_CANDLES_${cacheKey}_${Date.now()}_${Math.random()
      .toString(36)
      .slice(2, 8)}`;
    let settled = false;
    const finish = (out: HourlyPoint[] | Error) => {
      if (settled) return;
      settled = true;
      if (ws.unsubscribeFromChannel) {
        try {
          ws.unsubscribeFromChannel(reqId);
        } catch {
          /* ignore */
        }
      }
      candleInflight.delete(cacheKey);
      if (out instanceof Error) {
        reject(out);
      } else {
        candleCache.set(cacheKey, out);
        resolve(out);
      }
    };

    ws.subscribeToChannel(reqId, (raw: unknown) => {
      const resp = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : undefined;
      if (resp?.error) {
        finish(new Error(`candlestick error (${cacheKey}): ${JSON.stringify(resp.error)}`));
        return;
      }
      const result = resp?.result;
      if (!Array.isArray(result)) {
        finish(new Error(`candlestick: unexpected response shape (${cacheKey})`));
        return;
      }
      finish(result.map(toHourlyPoint));
    });

    ws.sendMessage(
      JSON.stringify(buildPerpCandleRequest(reqId, target, begin.toISOString(), end.toISOString())),
    );

    setTimeout(() => {
      if (!settled) finish(new Error(`candlestick timed out (${cacheKey})`));
    }, CANDLE_TIMEOUT_MS);
  });

  candleInflight.set(cacheKey, promise);
  return promise;
};

// Subscribes to a token's latest_vol_surface and streams the raw payloads to
// `onSurface`. The surface is resolved by base_quote alone — a generated
// token's base_quote yields its AUTO-GENERATED surface, ETH/BTC yield the
// Exchange/Immersive surface — so there is no separate override param to set.
// Returns an unsubscribe fn.
//
// SUBSCRIPTION ROUTING (see websocket-context/handle-messages.ts): the request
// id MUST contain a SUBSCRIPTIONS_KEY value. latest_vol_surface is a channel —
// the server first answers the subscribe with an open-channel reply
// { id, result: <channel_id> }, and only isSubscriptionKey(id) ids get that
// channel recorded in activeChannels. Without it the open-channel reply falls
// through to the raw id handler (so onSurface receives the numeric ack, which
// carries no `tenors`) AND the real `method:'update'` push can never route back
// by channel_id — the surface would stay stuck on the ack forever. Prefixing
// with SNAPSHOT_DATA is the same contract snapshot-provider uses for this method.
export const subscribeSurface = (
  ws: WsHandle,
  token: string,
  onSurface: (payload: SurfacePayload | null) => void,
  onError: (message: string) => void,
): (() => void) => {
  const reqId = `${SUBSCRIPTIONS_KEY.SNAPSHOT_DATA}_ALT_SURFACE_VS_${token}_${Date.now()}_${Math.random()
    .toString(36)
    .slice(2, 8)}`;
  let gotFirst = false;
  // latest_vol_surface is a STREAMING channel (periodic `update` pushes). After
  // the subscription is torn down on token change, a buffered / in-flight push
  // from the previous token's channel can still fire this handler and land the
  // previous token's payload on the current selection (IM-2487). The channel
  // push carries no token/symbol in params.data to key off, so this guard — set
  // true by cleanup — is the one thing that stops a dead subscription from
  // calling onSurface/onError after teardown.
  let cancelled = false;

  ws.subscribeToChannel(reqId, (raw: unknown) => {
    if (cancelled) return;
    const resp = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : undefined;
    if (resp?.error) {
      onError(`latest_vol_surface error (${token}): ${JSON.stringify(resp.error)}`);
      return;
    }
    gotFirst = true;
    onSurface(resp ?? null);
  });

  ws.sendMessage(
    JSON.stringify(
      new SnapshotRequest(reqId, token, SnapshotTypeEnum.EE, new Date().toISOString()),
    ),
  );

  setTimeout(() => {
    if (cancelled) return;
    if (!gotFirst) {
      onError(`latest_vol_surface timed out (${token})`);
    }
  }, SURFACE_TIMEOUT_MS);

  return () => {
    cancelled = true;
    if (ws.unsubscribeFromChannel) {
      try {
        ws.unsubscribeFromChannel(reqId);
      } catch {
        /* ignore */
      }
    }
  };
};

// Inputs the hook needs resolved from the registry entry for the selected
// token: its wire base_quote (surface key) and the perp candle target.
export interface AltSurfaceToken {
  baseQuote: string;
  source: string;
  exchangeSymbol: string;
}

export const altSurfaceTokenFromRegistry = (token: AvailableToken): AltSurfaceToken => ({
  baseQuote: token.baseQuote,
  source: token.source,
  exchangeSymbol: token.exchangeSymbol,
});

export const useAltSurfaceInputs = (token: AltSurfaceToken | null): AltSurfaceInputs => {
  const { sendMessage, subscribeToChannel, unsubscribeFromChannel, isConnected } =
    useContext(WebSocketContext);

  const [dailyCloses, setDailyCloses] = useState<DailyCloseRow[]>([]);
  const [hourlyCloses, setHourlyCloses] = useState<DailyCloseRow[]>([]);
  const [ethSurface, setEthSurface] = useState<SurfacePayload | null>(null);
  const [btcSurface, setBtcSurface] = useState<SurfacePayload | null>(null);
  const [publishedTokenSurface, setPublishedTokenSurface] = useState<SurfacePayload | null>(null);
  const [candlesLoading, setCandlesLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Keep the WS handle in a ref so fetch closures always see the live context
  // without widening effect deps (the context object identity is stable per
  // connection but the callbacks are rebuilt on reconnect).
  const wsRef = useRef<WsHandle>({ sendMessage, subscribeToChannel, unsubscribeFromChannel });
  useEffect(() => {
    wsRef.current = { sendMessage, subscribeToChannel, unsubscribeFromChannel };
  });

  // Derive PRIMITIVES off the token, never depend on the object itself: the
  // caller builds a fresh AltSurfaceToken literal every render, so an
  // object-identity dep would re-fire the effect → setDailyCloses → re-render →
  // loop ("Maximum update depth exceeded"). Primitives are value-stable, so the
  // effect settles after the first run and only re-fires on a real change.
  const baseQuote = token?.baseQuote ?? null;
  const source = token?.source ?? null;
  const exchangeSymbol = token?.exchangeSymbol ?? null;

  // Candle fetch: ETH + BTC (shared) and the selected token (per base_quote).
  useEffect(() => {
    if (!isConnected || !baseQuote || !source || !exchangeSymbol) return;
    let cancelled = false;
    // Clear the previous token's frame up front: this effect only re-fires on a
    // real token change (primitive deps) or reconnect, and the old closes must
    // never render as the current token. A failed/empty fetch then leaves the
    // frame empty (the catch does not repopulate it), so the screen shows the
    // loading/empty/error state, not another token's series.
    setDailyCloses([]);
    setHourlyCloses([]);
    setCandlesLoading(true);
    setError(null);

    const ws = wsRef.current;
    const tokenTarget = resolveTokenPerpTarget({ source, exchangeSymbol, baseQuote });

    Promise.all([
      fetchCandles(ws, DAILY_CLOSE_COLUMNS.ETH, ETH_PERP_TARGET),
      fetchCandles(ws, DAILY_CLOSE_COLUMNS.BTC, BTC_PERP_TARGET),
      fetchCandles(ws, `TOKEN_${baseQuote}`, tokenTarget),
    ])
      .then(([eth, btc, tokenSeries]) => {
        if (cancelled) return;
        // Both frames come from the one fetched set of candles: the daily frame
        // resamples to UTC daily-last, the hourly frame keeps the raw hourly
        // instants. No second fetch — the hourly series is already in hand.
        const seriesByColumn = {
          [DAILY_CLOSE_COLUMNS.ETH]: eth,
          [DAILY_CLOSE_COLUMNS.BTC]: btc,
          [DAILY_CLOSE_COLUMNS.TOKEN]: tokenSeries,
        };
        setDailyCloses(assembleDailyCloseFrame(seriesByColumn));
        setHourlyCloses(assembleHourlyCloseFrame(seriesByColumn));
        setCandlesLoading(false);
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setError(e.message);
        setCandlesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isConnected, baseQuote, source, exchangeSymbol]);

  // ETH/BTC reference surfaces: ETH is the ATM anchor, BTC supplies the discount
  // factors for token futures. Both are shared across every token, so they are
  // subscribed ONCE per connection — gated on isConnected alone. Re-subscribing
  // on each token change would re-fire two redundant snapshot requests at the
  // single-threaded gateway for surfaces that never vary by token.
  useEffect(() => {
    if (!isConnected) return;
    const ws = wsRef.current;
    const onError = (message: string) => setError((prev) => prev ?? message);

    const unsubs = [
      subscribeSurface(ws, DAILY_CLOSE_COLUMNS.ETH, setEthSurface, onError),
      subscribeSurface(ws, DAILY_CLOSE_COLUMNS.BTC, setBtcSurface, onError),
    ];

    return () => unsubs.forEach((unsub) => unsub());
  }, [isConnected]);

  // Token published (AUTO-GENERATED) surface: the tie-out source and the as-of
  // for the term structure. This one re-subscribes per token, and is cleared on
  // token change so a stale surface never bleeds into the next selection.
  useEffect(() => {
    if (!isConnected || !baseQuote) return;
    const ws = wsRef.current;
    const onError = (message: string) => setError((prev) => prev ?? message);

    const unsub = subscribeSurface(ws, baseQuote, setPublishedTokenSurface, onError);

    return () => {
      unsub();
      setPublishedTokenSurface(null);
    };
  }, [isConnected, baseQuote]);

  // Token spot. FLAGGED (IM-2486): Deribit futures_snapshot (the FE's only spot
  // path, use-futures-snapshot.ts) is Deribit-only, so it can't price a
  // binance/mexc generated token. We use the latest token perp close from the
  // already-fetched daily frame as the spot proxy — no new transport invented.
  // If a true spot (distinct from perp mark) is needed, that is a follow-up.
  const tokenSpot = useMemo(() => latestTokenClose(dailyCloses), [dailyCloses]);

  // A selected token is "ready" only once connected AND its surface has
  // arrived. While disconnected we are NOT ready — reporting isLoading keeps
  // the screen from falsely claiming "Ready — 0 daily closes" when it is simply
  // waiting on the socket.
  const isLoading = useMemo(
    () =>
      candlesLoading || (!!baseQuote && !error && (!isConnected || publishedTokenSurface === null)),
    [candlesLoading, baseQuote, isConnected, publishedTokenSurface, error],
  );

  return useMemo(
    () => ({
      dailyCloses,
      hourlyCloses,
      ethSurface,
      btcSurface,
      publishedTokenSurface,
      tokenSpot,
      isLoading,
      error,
    }),
    [
      dailyCloses,
      hourlyCloses,
      ethSurface,
      btcSurface,
      publishedTokenSurface,
      tokenSpot,
      isLoading,
      error,
    ],
  );
};
